// apple-pay.mjs

class JsResult {
    constructor(data = null, error = null) {
        this.data = data;
        this.error = error;
    }

    static success(data) {
        return new JsResult(data, null);
    }

    static error(errorMessage) {
        return new JsResult(null, errorMessage);
    }

    get isSuccess() {
        return this.error === null;
    }

    get isError() {
        return this.error !== null;
    }
}

/**
 * Check if Apple Pay is available
 */
export function isApplePayAvailable() {
    console.log("[JS] Checking Apple Pay availability");

    if (!window.ApplePaySession) {
        console.warn("[JS] ApplePaySession not available");
        return JsResult.error("Apple Pay not available");
    }

    if (!ApplePaySession.canMakePayments()) {
        console.warn("[JS] Device cannot make Apple Pay payments");
        return JsResult.error("Device cannot make payments");
    }

    console.log("[JS] Apple Pay is available");
    return JsResult.success(true);
}

/**
 * Create Apple Pay payment request
 */
export function createPaymentRequest(
    merchantId,
    merchantName,
    applicationData,
    countryCode = "PL",
    currencyCode = "PLN",
    totalPrice,
    supportedNetworks = ['visa', 'masterCard', 'amex', 'discover'],
    merchantCapabilities = ['supports3DS']
) {
    console.log("[JS] Creating Apple Pay payment request");

    try {
        const request = {
            countryCode: countryCode,
            currencyCode: currencyCode,
            supportedNetworks: supportedNetworks,
            merchantCapabilities: merchantCapabilities,
            applicationData: applicationData,
            total: {
                label: merchantName,
                amount: totalPrice
            }
        };

        console.log("[JS] Payment request created:", request);
        return JsResult.success(request);
    } catch (error) {
        console.error("[JS] Error creating payment request:", error);
        return JsResult.error(`Failed to create payment request: ${error.message}`);
    }
}

/**
 * Start Apple Pay payment session with merchant validation callback
 *
 * @param {object} paymentRequest - Payment request object
 * @param {string} merchantId - Merchant identifier
 * @param {function} onValidateMerchant - Callback to validate merchant (validationURL) => Promise<merchantSession>
 * @returns {Promise<JsResult>} Promise with payment token or error
 */
export function startPaymentSession(paymentRequest, merchantId, onValidateMerchant) {
    console.log("[JS] Starting Apple Pay session");

    return new Promise((resolve, reject) => {
        if (!window.ApplePaySession) {
            // Return error result instead of rejecting - WASM doesn't handle Promise rejections well
            resolve(JsResult.error("Apple Pay not available"));
            return;
        }

        try {
            // Create Apple Pay session (version 3)
            const session = new ApplePaySession(3, paymentRequest);

            // Handle merchant validation
            session.onvalidatemerchant = async (event) => {
                console.log("[JS] onvalidatemerchant event received");
                console.log("[JS] Validation URL:", event.validationURL);

                try {
                    // Call the Kotlin callback to validate merchant via backend
                    const merchantSession = await onValidateMerchant(event.validationURL);
                    console.log("[JS] Merchant session received from backend");

                    console.log("[JS] Merchant session:", merchantSession);
                    console.log("[JS] Merchant session:", typeof merchantSession);

                    // Complete merchant validation
                    if (typeof merchantSession === "string") {
                        const parsedMerchantSession = JSON.parse(merchantSession);
                        console.log("[JS] Parsed merchant session:", parsedMerchantSession);
                        session.completeMerchantValidation(parsedMerchantSession);
                    } else {
                        session.completeMerchantValidation(merchantSession);
                    }
                } catch (error) {
                    console.error("[JS] Merchant validation failed:", error);
                    session.abort();
                    // Return error result instead of rejecting
                    resolve(JsResult.error(`Merchant validation failed: ${error.message}`));
                }
            };

            // Handle payment authorization
            session.onpaymentauthorized = (event) => {
                console.log("[JS] onpaymentauthorized event received");

                // Complete payment immediately (we'll process token on backend)
                session.completePayment(ApplePaySession.STATUS_SUCCESS);

                // Resolve with the payment token
                resolve(JsResult.success(event.payment.token));
            };

            // Handle cancellation
            session.oncancel = (event) => {
                console.log("[JS] Apple Pay cancelled by user");
                console.log("[JS] User cancellation is normal - payment sheet dismissed without completing");
                // User cancellation is NOT an error - return specific error code for graceful handling
                resolve(JsResult.error("USER_CANCELLED_APPLE_PAY"));
            };

            // Start the session
            console.log("[JS] Beginning Apple Pay session");
            session.begin();

        } catch (error) {
            console.error("[JS] Error starting Apple Pay session:", error);
            // Return error result instead of rejecting
            resolve(JsResult.error(`Failed to start session: ${error.message}`));
        }
    });
}

/**
 * Extract token from Apple Pay payment data
 */
export function extractApplePayToken(paymentData) {
    console.log("[JS] Extracting Apple Pay token");
    console.log("[JS] Extracting Apple Pay token - paymentData:", paymentData);

    try {
        if (!paymentData || !paymentData.paymentData) {
            return JsResult.error("Invalid payment data");
        }

        return JsResult.success(paymentData.paymentData);
    } catch (error) {
        console.error("[JS] Error extracting token:", error);
        return JsResult.error(`Failed to extract token: ${error.message}`);
    }
}

/**
 * Stringify an object
 */
export function stringifyObject(obj) {
    console.log("[JS] Stringifying object");

    try {
        const stringified = JSON.stringify(obj);
        return JsResult.success(stringified);
    } catch (error) {
        console.error("[JS] Error stringifying object:", error);
        return JsResult.error(`Failed to stringify: ${error.message}`);
    }
}

// Export object with all functions
const ApplePayObj = {
    isApplePayAvailable,
    createPaymentRequest,
    startPaymentSession,
    extractApplePayToken,
    stringifyObject
};

globalThis.ApplePayObj = ApplePayObj;

export {ApplePayObj, JsResult};
